export const appsContent = {
  en: {
    shared: {
      apps: 'Apps',
      ruleForge: 'RuleForge',
      overview: 'Overview',
      support: 'Support',
      privacy: 'Privacy',
      terms: 'Terms',
      backToApps: 'All apps',
      appNavigation: 'RuleForge pages',
      contactEmail: 'hello@jamaklab.com',
      contactSupport: 'Contact support',
      updatedLabel: 'Effective',
      updatedDate: 'October 9, 2026'
    },
    hub: {
      metaTitle: 'Apps by Jamak | Practical software for growing teams',
      metaDescription: 'Explore focused apps built by Jamak, starting with RuleForge for advanced automatic discounts on Shopify.',
      eyebrow: 'Built by Jamak',
      title: 'Small tools. Serious leverage.',
      intro: 'We build focused software for the repetitive, high-stakes work that growing teams should not have to solve twice.',
      cardLabel: 'Shopify app · Free plan available',
      cardTitle: 'RuleForge',
      cardDescription: 'Build advanced automatic product and shipping discounts for customer groups, spend tiers, product targets, and exclusions, without editing code.',
      cardCta: 'Explore RuleForge',
      footerTitle: 'More useful software is in the forge.',
      footerBody: 'RuleForge is the first product in our public app collection. New releases will appear here.'
    },
    product: {
      metaTitle: 'RuleForge | Automatic Shopify discounts and free shipping by rule',
      metaDescription: 'RuleForge applies automatic product discounts and free shipping in Shopify checkout based on customer tags, order totals, and the products in the cart. No discount codes, no code to write.',
      eyebrow: 'Shopify app · Built on Shopify Functions',
      title: 'Automatic discounts for the right customers',
      titleLines: ['Automatic discounts', 'for the right customers'],
      intro: 'RuleForge applies product discounts and free shipping in Shopify checkout based on customer tags, order totals, and the products in the cart. No codes for shoppers to remember. No code for you to write.',
      primaryCta: 'Get RuleForge free',
      proof: ['Free plan with 3 active rules', 'Billing handled by Shopify', 'Uninstall any time'],
      preview: {
        label: 'Example RuleForge workspace with three active rules and a checkout preview for a VIP customer',
        windowTitle: 'Discounts',
        activeCount: '3 of 3 active',
        rules: [
          { name: 'VIP members', detail: 'Tag vip · 12% off all products', active: true },
          { name: 'Wholesale buyers', detail: 'Tag wholesale · $3.50 off each item tagged bulk', active: true },
          { name: 'Free shipping', detail: 'Orders from $85.00', active: true },
          { name: 'Staff pricing', detail: 'Tag staff · 20% off · draft', active: false }
        ],
        checkoutTitle: 'Checkout',
        checkoutTag: 'vip',
        subtotalLabel: 'Subtotal',
        subtotal: '$142.00',
        discountLabel: 'VIP members',
        discount: '−$17.04',
        shippingLabel: 'Shipping',
        shipping: 'Free',
        totalLabel: 'Total',
        total: '$124.96'
      },
      strip: [
        'Runs natively in Shopify checkout',
        'Plans billed on your Shopify invoice',
        'Rules saved in your own store',
        'No shopper data kept for marketing'
      ],
      problemTitle: 'Discount codes put the work on your customers',
      problemIntro: 'Codes get shared, forgotten, or typed wrong, and every new promotion means another one to manage. RuleForge lets the cart decide.',
      before: {
        label: 'With codes',
        title: 'Manual and easy to leak',
        items: [
          'Loyal customers miss out when they forget the code',
          'Codes end up on coupon sites and reach everyone',
          'A separate code for VIP, wholesale, and staff',
          'No easy way to check a promotion before launch'
        ]
      },
      after: {
        label: 'With RuleForge',
        title: 'Automatic and controlled',
        items: [
          'Discounts apply on their own when a cart matches',
          'Only tagged customers or qualifying orders get the price',
          'Every group and shipping rule lives in one workspace',
          'Preview a cart before you turn anything on'
        ]
      },
      tagline: {
        lead: 'The right price for every customer,',
        accent: 'applied at checkout',
        tail: 'without a single code.'
      },
      featuresTitle: 'Everything you need for rule based discounts',
      featuresIntro: 'Set up once inside Shopify admin. RuleForge keeps applying your rules on every order.',
      features: [
        {
          title: 'Reward customer groups',
          description: 'Give VIP, wholesale, or staff customers their own price with a customer tag. Choose a percentage or a fixed amount off each item, with an optional order minimum.'
        },
        {
          title: 'Free shipping that earns its keep',
          description: 'Unlock free shipping from an order minimum, a customer tag, or both, so you only cover shipping on the orders you want.'
        },
        {
          title: 'Target exactly what is on sale',
          description: 'Apply discounts to all products, product tags, specific products, or individual variants, and exclude the items you never discount.'
        },
        {
          title: 'Test before customers see it',
          description: 'New rules start turned off. The draft checkout simulator shows what a cart would pay, so you launch with confidence instead of guesswork.'
        },
        {
          title: 'Know everything is connected',
          description: 'System checks confirm the Shopify discount, the checkout Function, your rules, and your plan are wired together, with plain language guidance when something needs attention.'
        }
      ],
      workflowTitle: 'Live in three steps',
      workflowIntro: 'No theme edits and no developer needed.',
      workflow: [
        {
          step: '1',
          title: 'Install from Shopify',
          description: 'Add RuleForge through Shopify’s standard install flow. It opens inside your admin, with no extra account to create.'
        },
        {
          step: '2',
          title: 'Build your rules',
          description: 'Start from a ready made rule or create your own. Pick the customer tag, the products, and the discount amount.'
        },
        {
          step: '3',
          title: 'Preview and sync',
          description: 'Check a sample cart in the simulator, turn the rule on, and sync to Shopify. Your discount is live at checkout.'
        }
      ],
      useCasesTitle: 'Promotions your store already runs, on autopilot',
      useCasesIntro: 'Whether you sell to regulars, resellers, or your own team, RuleForge turns the deal into a rule.',
      ruleLabel: 'Rule',
      useCases: [
        {
          title: 'Thank your regulars',
          description: 'Tag returning customers and give them a standing discount they never have to ask for.',
          rule: 'Tag vip · 12% off all products'
        },
        {
          title: 'Sell to resellers',
          description: 'Offer wholesale pricing on bulk items to approved buyers while retail shoppers see regular prices.',
          rule: 'Tag wholesale · $3.50 off each item tagged bulk'
        },
        {
          title: 'Grow the average order',
          description: 'Show shoppers a clear goal: spend past your threshold and shipping is on you.',
          rule: 'Orders from $85.00 · free shipping'
        }
      ],
      plansTitle: 'Start free, upgrade when you grow',
      plansIntro: 'Both plans include every feature. The only difference is how many rules can be active at once.',
      plansUnit: 'active rules',
      plansNote: 'Current prices and any trial are shown on Shopify’s plan page when you install or upgrade.',
      plans: [
        {
          name: 'Free',
          limit: '3',
          featured: false,
          items: [
            'Customer tag and order minimum rules',
            'Free shipping rules',
            'Draft checkout simulator',
            'System checks and setup guidance'
          ]
        },
        {
          name: 'Pro',
          limit: '20',
          featured: true,
          items: [
            'Everything in Free',
            'Room for every customer group and promotion',
            'Change or cancel from Shopify admin',
            'Billed on your Shopify invoice'
          ]
        }
      ],
      faqTitle: 'Questions merchants ask',
      faq: [
        {
          question: 'Do my customers need to enter a discount code?',
          answer: 'No. RuleForge creates an automatic discount in Shopify. When a cart matches one of your rules, the discount appears at checkout on its own.'
        },
        {
          question: 'Who can receive a RuleForge discount?',
          answer: 'You decide with customer tags and optional order minimums. Discounts can target all products, product tags, specific products, or specific variants, and you can exclude items you never want to discount.'
        },
        {
          question: 'Can I offer free shipping?',
          answer: 'Yes. Free shipping rules can match a customer tag, an order minimum, or both.'
        },
        {
          question: 'How do I test a rule before customers see it?',
          answer: 'New rules start turned off. Use the draft checkout simulator to preview a cart, then turn the rule on and sync it to Shopify when you are ready.'
        },
        {
          question: 'How much does RuleForge cost?',
          answer: 'There is a Free plan with up to 3 active rules and a Pro plan with up to 20. Current prices and any trial are shown on the plan page hosted by Shopify, and charges appear on your Shopify invoice.'
        },
        {
          question: 'Will RuleForge slow down my checkout?',
          answer: 'Discounts run inside Shopify checkout on Shopify Functions. Every release is checked against the performance limits Shopify sets for Functions, including large carts.'
        },
        {
          question: 'Is RuleForge a Shopify Scripts replacement?',
          answer: 'No. RuleForge focuses on product and shipping discounts by rule. It is not a migration path for Shopify Scripts.'
        },
        {
          question: 'Does RuleForge support BOGO, bundles, or volume pricing?',
          answer: 'Not today. RuleForge supports percentage and fixed amount per item product discounts, plus free shipping.'
        },
        {
          question: 'Can RuleForge discounts combine with my other Shopify discounts?',
          answer: 'Not at the moment. Combination with other discount classes is not enabled, so plan overlapping promotions with that in mind.'
        },
        {
          question: 'What store data does RuleForge access?',
          answer: 'RuleForge asks Shopify for permission to read products and to read and write discounts. It does not store your shoppers’ personal data for marketing. The privacy policy explains every detail.'
        }
      ],
      finalTitle: 'Your next promotion can run itself',
      finalBody: 'Install RuleForge, build your first rule in minutes, and let checkout handle the rest.',
      risk: ['Free plan, no card on file with us', 'Rules start off until you enable them', 'Uninstall any time from Shopify admin'],
      resourceTitle: 'Help and policies',
      resources: [
        {
          key: 'support',
          title: 'Support',
          description: 'Setup guidance, testing steps, troubleshooting, and a direct contact channel.'
        },
        {
          key: 'privacy',
          title: 'Privacy policy',
          description: 'What RuleForge processes, why it is needed, and how data requests are handled.'
        },
        {
          key: 'terms',
          title: 'Terms of service',
          description: 'The rules and responsibilities that apply when using RuleForge.'
        }
      ]
    },
    support: {
      metaTitle: 'RuleForge Support | Help for Shopify merchants',
      metaDescription: 'Get help setting up, testing, and troubleshooting RuleForge automatic discounts for Shopify.',
      eyebrow: 'RuleForge support',
      title: 'Get your rules working with confidence.',
      intro: 'Use this guide for setup, checkout testing, and common troubleshooting. If you still need help, email the Jamak team and include your Shopify store domain.',
      contactTitle: 'Need a human?',
      contactBody: 'Email us with your shop domain, what you expected to happen, and what happened instead. Do not send customer passwords, payment details, or Shopify access tokens.',
      contactCta: 'Email RuleForge support',
      sections: [
        {
          heading: 'Getting started',
          ordered: true,
          items: [
            'Install RuleForge and open it from Shopify Admin.',
            'Create a customer group and add the Shopify customer tags that make a shopper eligible.',
            'Add one or more product discount tiers, a shipping rule, or both.',
            'Review exclusions, save your changes, and confirm that the in-app system checks are ready.'
          ]
        },
        {
          heading: 'Test before promoting an offer',
          paragraphs: [
            'Use a customer account that has the configured tag, add an eligible product to the cart, and continue to checkout. Confirm both the product discount message and any shipping discount before publishing the offer.',
            'Automatic discounts do not apply to Shopify draft orders by default. For a draft order, choose “Apply all eligible automatic discounts” before comparing the result.'
          ]
        },
        {
          heading: 'If a discount does not appear',
          items: [
            'Confirm the rule and the relevant tier are enabled.',
            'Confirm the test customer is logged in and has the exact customer tag used by the rule.',
            'Check that the subtotal meets the configured threshold.',
            'Check global and customer-specific product exclusions.',
            'If you target product tags or variants, confirm the cart line matches those selectors.',
            'Open System checks in RuleForge and use Sync rules if the automatic discount or Function link needs attention.'
          ]
        },
        {
          heading: 'Before contacting support',
          items: [
            'Note the store domain and the approximate time of the test.',
            'Share the rule name, expected discount, and the product or variant tested.',
            'Include a screenshot of the RuleForge system checks and checkout result when possible.',
            'Never include credentials, access tokens, or full customer payment information.'
          ]
        },
        {
          heading: 'Data and deletion requests',
          paragraphs: [
            'For privacy questions or a request related to your merchant data, email hello@jamaklab.com from an address associated with the store. Shopify customer data requests are handled through Shopify’s required compliance process.'
          ]
        }
      ]
    },
    privacy: {
      metaTitle: 'RuleForge Privacy Policy | JAMAK',
      metaDescription: 'Learn what data RuleForge processes, how it is used, and how Jamak handles privacy and deletion requests.',
      eyebrow: 'RuleForge privacy',
      title: 'Privacy policy',
      intro: 'This policy explains how RuleForge, a Shopify app provided by Jamak, processes information when a merchant installs or uses the app.',
      sections: [
        {
          heading: 'Information we process',
          items: [
            'Shop and account information supplied by Shopify, including the shop domain, the access token used to call the Shopify Admin API, and installation or authentication records needed to connect the app. Session records may include staff identity fields Shopify provides at sign-in, such as name, email, and locale.',
            'Rule configuration, including customer-tag conditions, spend thresholds, product selectors, exclusions, discount values, messages, and shipping rules.',
            'Plan status, including the active plan, its rule limit, and when it was last verified with Shopify.',
            'Sample carts a merchant enters in the draft checkout simulator, processed to calculate the preview.',
            'Technical and diagnostic information such as request timestamps, application errors, and security or availability events needed to operate and protect the service.'
          ]
        },
        {
          heading: 'Customer and order data',
          paragraphs: [
            'RuleForge does not persist customer profiles, order records, or payment information in its own database. At checkout, Shopify provides the configured Function with the cart, eligible product information, and customer tags needed to evaluate a rule. That evaluation occurs inside Shopify’s Function runtime.',
            'RuleForge requests only the Shopify permissions required to read products and discounts and to create or update app-managed discounts.'
          ]
        },
        {
          heading: 'How we use information',
          items: [
            'Authenticate the merchant and provide the app inside Shopify Admin.',
            'Create, update, validate, and troubleshoot the merchant’s automatic discount configuration.',
            'Maintain security, prevent abuse, diagnose errors, and improve service reliability.',
            'Respond to support, privacy, and legal requests.'
          ]
        },
        {
          heading: 'Where configuration is stored',
          paragraphs: [
            'Rule configuration is stored on the Shopify automatic discount as app-owned metafields. Authentication sessions and plan status are stored in a managed database so the app can securely communicate with Shopify. Shopify controls data processed within its platform and Function runtime under Shopify’s own terms and privacy practices.'
          ]
        },
        {
          heading: 'Billing',
          paragraphs: [
            'Paid plans are billed by Shopify through Shopify App Pricing and appear on the merchant’s Shopify invoice. We never receive or store card numbers or other payment credentials.'
          ]
        },
        {
          heading: 'Product analytics',
          paragraphs: [
            'RuleForge uses PostHog for limited product analytics. The app records which admin screen is open; app install and uninstall; rule create, enable, disable, and sync; and verified plan changes. A shop is represented by a salted one-way hash, not by its domain, and development stores are marked with a single yes-or-no value.',
            'RuleForge does not send staff, customer, order, URL, form, discount-rule contents, or billing amounts to PostHog. Automatic click and page tracking, person profiles, persistent browser identifiers, session replay, surveys, and IP geolocation are turned off.'
          ]
        },
        {
          heading: 'Error monitoring',
          paragraphs: [
            'We use Sentry to receive reports when the app encounters an error. Reports are limited to the error type, where it happened in our code, the app release, and short operational codes. They exclude user identity, cookies, request and response headers and bodies, URL query parameters, database data, and session replay.'
          ]
        },
        {
          heading: 'Service providers and disclosure',
          paragraphs: [
            'We share information only with the providers needed to run RuleForge: Shopify (platform, authentication, checkout, and billing), Railway (application hosting and managed database), Sentry (error monitoring), and PostHog (product analytics), each limited as described above. They may process information only to provide those services to us. We may also disclose information when required by law, to protect rights and safety, or as part of a business transaction subject to appropriate safeguards.',
            'We do not sell merchant or customer personal information.'
          ]
        },
        {
          heading: 'Retention and deletion',
          paragraphs: [
            'We keep merchant authentication and operational records only for as long as reasonably necessary to provide RuleForge, meet legal obligations, resolve disputes, and protect the service. After uninstallation, Shopify notifies RuleForge and the app removes the active session for that shop. When Shopify requests shop redaction, the app deletes the shop’s remaining session and billing records. App-owned configuration stored on Shopify resources remains subject to Shopify’s platform behavior and merchant controls.',
            'Verified deletion and access requests can be sent to hello@jamaklab.com. We also process Shopify’s mandatory shop and customer privacy webhooks.'
          ]
        },
        {
          heading: 'Security and international processing',
          paragraphs: [
            'We use administrative, technical, and organizational safeguards designed to protect information. No system is completely secure, and we cannot guarantee absolute security.',
            'Jamak and its service providers may process information in countries other than the merchant’s country. Where required, we use appropriate safeguards for those transfers.'
          ]
        },
        {
          heading: 'Your choices and rights',
          paragraphs: [
            'Depending on applicable law, you may have rights to request access, correction, deletion, restriction, or a copy of personal information. We may need to verify your identity and authority for the relevant shop before responding.'
          ]
        },
        {
          heading: 'Changes and contact',
          paragraphs: [
            'We may update this policy as RuleForge or applicable requirements change. The effective date on this page identifies the latest version. Questions and requests may be sent to hello@jamaklab.com.'
          ]
        }
      ]
    },
    terms: {
      metaTitle: 'RuleForge Terms of Service | JAMAK',
      metaDescription: 'Review the terms that apply when merchants install and use the RuleForge Shopify app.',
      eyebrow: 'RuleForge terms',
      title: 'Terms of service',
      intro: 'These terms govern access to and use of RuleForge, a Shopify app provided by Jamak. By installing or using RuleForge, you agree to these terms.',
      sections: [
        {
          heading: 'Eligibility and authority',
          paragraphs: [
            'You must be legally able to enter into these terms and authorized to act for the Shopify store that installs RuleForge. You are responsible for anyone who accesses the app through your store account.'
          ]
        },
        {
          heading: 'The service',
          paragraphs: [
            'RuleForge helps merchants configure automatic product and shipping discounts executed through Shopify Functions. It is not a Shopify Scripts replacement and does not provide buy-one-get-one offers, bundles, or quantity-based pricing, and its discounts do not combine with other Shopify discount classes. Features may evolve as we improve the app, and some functionality may be identified as beta or early access.'
          ]
        },
        {
          heading: 'Shopify access and permissions',
          paragraphs: [
            'You authorize RuleForge to use the Shopify permissions shown during installation to provide the service. Shopify is a separate provider, and your use of Shopify remains subject to Shopify’s agreements and policies.'
          ]
        },
        {
          heading: 'Merchant responsibilities',
          items: [
            'Configure accurate rules and test them before advertising or relying on an offer.',
            'Ensure discounts, pricing, marketing, and customer communications comply with applicable laws and your own policies.',
            'Maintain the security of your Shopify account and promptly report suspected unauthorized access.',
            'Provide accurate information when requesting support.'
          ]
        },
        {
          heading: 'Fees',
          paragraphs: [
            'RuleForge offers a Free plan with up to 3 active rules and a Pro plan with up to 20. Paid plans are billed only through Shopify App Pricing and appear on your Shopify invoice; there are no charges outside Shopify. Current prices and any trial are shown on Shopify’s plan page before you approve a charge, and you can change or cancel a plan from Shopify Admin. If a paid plan cannot be verified with Shopify, the app falls back to Free plan limits.'
          ]
        },
        {
          heading: 'Acceptable use',
          paragraphs: [
            'You may not misuse RuleForge, interfere with its operation, attempt unauthorized access, reverse engineer the service except where law permits, use it to violate law or third-party rights, or submit malicious code or content.'
          ]
        },
        {
          heading: 'Availability and changes',
          paragraphs: [
            'We aim to provide a reliable service, but RuleForge may occasionally be unavailable because of maintenance, security work, Shopify platform changes, or events outside our control. We may modify or discontinue features and will provide notice when reasonably practical for material changes.'
          ]
        },
        {
          heading: 'Ownership and feedback',
          paragraphs: [
            'Jamak and its licensors retain all rights in RuleForge, excluding merchant content and data. You receive a limited, non-exclusive, non-transferable right to use the app for your store while these terms apply. If you provide feedback, you allow us to use it without restriction or compensation.'
          ]
        },
        {
          heading: 'Termination',
          paragraphs: [
            'You may stop using RuleForge at any time by uninstalling it. We may suspend or terminate access if you materially breach these terms, create a security or legal risk, or if continued operation is no longer practical. Provisions that by their nature should survive termination will remain in effect.'
          ]
        },
        {
          heading: 'Disclaimers',
          paragraphs: [
            'To the extent permitted by law, RuleForge is provided “as is” and “as available.” We disclaim implied warranties, including merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that every configuration will produce a particular commercial result or remain compatible with every third-party service.'
          ]
        },
        {
          heading: 'Limitation of liability',
          paragraphs: [
            'To the maximum extent permitted by law, Jamak will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, revenue, data, goodwill, or business opportunities arising from RuleForge. Our aggregate liability relating to the service will not exceed the greater of the amount you paid for RuleForge during the twelve months before the event giving rise to the claim or USD $100.'
          ]
        },
        {
          heading: 'Governing law and contact',
          paragraphs: [
            'These terms are governed by the applicable laws of Mexico, without regard to conflict-of-law principles. Questions about these terms may be sent to hello@jamaklab.com.'
          ]
        }
      ]
    }
  },
  es: {
    shared: {
      apps: 'Apps',
      ruleForge: 'RuleForge',
      overview: 'Descripción',
      support: 'Soporte',
      privacy: 'Privacidad',
      terms: 'Términos',
      backToApps: 'Todas las apps',
      appNavigation: 'Páginas de RuleForge',
      contactEmail: 'hello@jamaklab.com',
      contactSupport: 'Contactar a soporte',
      updatedLabel: 'Vigente desde',
      updatedDate: '9 de octubre de 2026'
    },
    hub: {
      metaTitle: 'Apps de Jamak | Software práctico para equipos en crecimiento',
      metaDescription: 'Conoce las apps creadas por Jamak, comenzando con RuleForge para descuentos automáticos avanzados en Shopify.',
      eyebrow: 'Creado por Jamak',
      title: 'Herramientas pequeñas. Ventajas enormes.',
      intro: 'Creamos software enfocado para el trabajo repetitivo y crítico que un equipo en crecimiento no debería resolver dos veces.',
      cardLabel: 'App de Shopify · Plan gratis disponible',
      cardTitle: 'RuleForge',
      cardDescription: 'Crea descuentos automáticos avanzados de producto y envío para grupos de clientes, niveles de gasto, productos objetivo y exclusiones, sin editar código.',
      cardCta: 'Conocer RuleForge',
      footerTitle: 'Más software útil está en la forja.',
      footerBody: 'RuleForge es el primer producto de nuestra colección pública de apps. Los próximos lanzamientos aparecerán aquí.'
    },
    product: {
      metaTitle: 'RuleForge | Descuentos automáticos y envío gratis por reglas en Shopify',
      metaDescription: 'RuleForge aplica descuentos automáticos de producto y envío gratis en el checkout de Shopify según etiquetas de cliente, el total del pedido y los productos del carrito. Sin cupones y sin escribir código.',
      eyebrow: 'App de Shopify · Hecha con Shopify Functions',
      title: 'Descuentos automáticos para los clientes correctos',
      titleLines: ['Descuentos automáticos', 'para los clientes correctos'],
      intro: 'RuleForge aplica descuentos de producto y envío gratis en el checkout de Shopify según las etiquetas del cliente, el total del pedido y los productos del carrito. Tus clientes no tienen que recordar cupones y tú no tienes que escribir código.',
      primaryCta: 'Obtener RuleForge gratis',
      proof: ['Plan gratis con 3 reglas activas', 'Cobro gestionado por Shopify', 'Desinstala cuando quieras'],
      preview: {
        label: 'Ejemplo del espacio de RuleForge con tres reglas activas y la vista previa del checkout de un cliente VIP',
        windowTitle: 'Descuentos',
        activeCount: '3 de 3 activas',
        rules: [
          { name: 'Clientes VIP', detail: 'Etiqueta vip · 12% en todos los productos', active: true },
          { name: 'Mayoristas', detail: 'Etiqueta mayoreo · $3.50 menos por pieza con etiqueta granel', active: true },
          { name: 'Envío gratis', detail: 'Pedidos desde $85.00', active: true },
          { name: 'Precio para el equipo', detail: 'Etiqueta staff · 20% · borrador', active: false }
        ],
        checkoutTitle: 'Checkout',
        checkoutTag: 'vip',
        subtotalLabel: 'Subtotal',
        subtotal: '$142.00',
        discountLabel: 'Clientes VIP',
        discount: '−$17.04',
        shippingLabel: 'Envío',
        shipping: 'Gratis',
        totalLabel: 'Total',
        total: '$124.96'
      },
      strip: [
        'Funciona dentro del checkout de Shopify',
        'Planes cobrados en tu factura de Shopify',
        'Reglas guardadas en tu propia tienda',
        'No guardamos datos de compradores para marketing'
      ],
      problemTitle: 'Los cupones le dejan el trabajo a tus clientes',
      problemIntro: 'Los códigos se comparten, se olvidan o se escriben mal, y cada promoción nueva es otro código que administrar. Con RuleForge, el carrito decide.',
      before: {
        label: 'Con cupones',
        title: 'Manual y fácil de filtrar',
        items: [
          'Tus clientes leales pierden el descuento si olvidan el código',
          'Los códigos terminan en sitios de cupones y llegan a cualquiera',
          'Un código distinto para VIP, mayoreo y equipo',
          'No hay forma sencilla de revisar una promoción antes de lanzarla'
        ]
      },
      after: {
        label: 'Con RuleForge',
        title: 'Automático y bajo control',
        items: [
          'Los descuentos se aplican solos cuando el carrito califica',
          'Solo los clientes etiquetados o los pedidos que califican obtienen el precio',
          'Todos los grupos y reglas de envío viven en un solo espacio',
          'Revisa un carrito de prueba antes de activar algo'
        ]
      },
      tagline: {
        lead: 'El precio correcto para cada cliente,',
        accent: 'aplicado en el checkout',
        tail: 'sin un solo cupón.'
      },
      featuresTitle: 'Todo lo que necesitas para descuentos por reglas',
      featuresIntro: 'Configúralo una vez dentro del admin de Shopify. RuleForge sigue aplicando tus reglas en cada pedido.',
      features: [
        {
          title: 'Premia a tus grupos de clientes',
          description: 'Dale a tus clientes VIP, mayoristas o a tu equipo su propio precio con una etiqueta de cliente. Elige un porcentaje o un monto fijo por pieza, con un mínimo de compra opcional.'
        },
        {
          title: 'Envío gratis que sí conviene',
          description: 'Activa el envío gratis a partir de un mínimo de compra, una etiqueta de cliente o ambos, para cubrir el envío solo en los pedidos que tú quieras.'
        },
        {
          title: 'Elige exactamente qué tiene descuento',
          description: 'Aplica descuentos a todos los productos, a etiquetas de producto, a productos específicos o a variantes, y excluye lo que nunca descuentas.'
        },
        {
          title: 'Prueba antes de que lo vean tus clientes',
          description: 'Las reglas nuevas empiezan apagadas. El simulador de checkout muestra cuánto pagaría un carrito, para que lances con certeza y no a ciegas.'
        },
        {
          title: 'Comprueba que todo está conectado',
          description: 'Las verificaciones del sistema confirman que el descuento de Shopify, la Function de checkout, tus reglas y tu plan están enlazados, con instrucciones claras cuando algo necesita atención.'
        }
      ],
      workflowTitle: 'Listo en tres pasos',
      workflowIntro: 'Sin editar tu tema y sin contratar a un desarrollador.',
      workflow: [
        {
          step: '1',
          title: 'Instala desde Shopify',
          description: 'Agrega RuleForge con el proceso de instalación normal de Shopify. Se abre dentro de tu admin, sin crear otra cuenta.'
        },
        {
          step: '2',
          title: 'Crea tus reglas',
          description: 'Parte de una regla lista o crea la tuya. Elige la etiqueta de cliente, los productos y el monto del descuento.'
        },
        {
          step: '3',
          title: 'Revisa y sincroniza',
          description: 'Prueba un carrito en el simulador, activa la regla y sincroniza con Shopify. Tu descuento queda activo en el checkout.'
        }
      ],
      useCasesTitle: 'Las promociones que ya haces, en automático',
      useCasesIntro: 'Ya sea que vendas a clientes frecuentes, a revendedores o a tu propio equipo, RuleForge convierte la oferta en una regla.',
      ruleLabel: 'Regla',
      useCases: [
        {
          title: 'Agradece a tus clientes frecuentes',
          description: 'Etiqueta a quienes regresan y dales un descuento permanente que nunca tienen que pedir.',
          rule: 'Etiqueta vip · 12% en todos los productos'
        },
        {
          title: 'Vende a revendedores',
          description: 'Ofrece precio de mayoreo en productos a granel a compradores aprobados, mientras el público ve el precio normal.',
          rule: 'Etiqueta mayoreo · $3.50 menos por pieza con etiqueta granel'
        },
        {
          title: 'Sube el ticket promedio',
          description: 'Dale a tus compradores una meta clara: si superan tu mínimo, el envío corre por tu cuenta.',
          rule: 'Pedidos desde $85.00 · envío gratis'
        }
      ],
      plansTitle: 'Empieza gratis y crece cuando lo necesites',
      plansIntro: 'Ambos planes incluyen todas las funciones. La única diferencia es cuántas reglas puedes tener activas al mismo tiempo.',
      plansUnit: 'reglas activas',
      plansNote: 'Los precios vigentes y cualquier periodo de prueba aparecen en la página de planes de Shopify al instalar o cambiar de plan.',
      plans: [
        {
          name: 'Free',
          limit: '3',
          featured: false,
          items: [
            'Reglas por etiqueta de cliente y mínimo de compra',
            'Reglas de envío gratis',
            'Simulador de checkout',
            'Verificaciones del sistema y guía de configuración'
          ]
        },
        {
          name: 'Pro',
          limit: '20',
          featured: true,
          items: [
            'Todo lo del plan Free',
            'Espacio para todos tus grupos y promociones',
            'Cambia o cancela desde el admin de Shopify',
            'Se cobra en tu factura de Shopify'
          ]
        }
      ],
      faqTitle: 'Preguntas frecuentes',
      faq: [
        {
          question: '¿Mis clientes tienen que escribir un cupón?',
          answer: 'No. RuleForge crea un descuento automático en Shopify. Cuando un carrito cumple una de tus reglas, el descuento aparece solo en el checkout.'
        },
        {
          question: '¿Quién puede recibir un descuento de RuleForge?',
          answer: 'Tú lo decides con etiquetas de cliente y mínimos de compra opcionales. Los descuentos pueden aplicar a todos los productos, a etiquetas de producto, a productos específicos o a variantes, y puedes excluir lo que nunca quieras descontar.'
        },
        {
          question: '¿Puedo ofrecer envío gratis?',
          answer: 'Sí. Las reglas de envío gratis pueden depender de una etiqueta de cliente, de un mínimo de compra o de ambos.'
        },
        {
          question: '¿Cómo pruebo una regla antes de que la vean mis clientes?',
          answer: 'Las reglas nuevas empiezan apagadas. Usa el simulador de checkout para revisar un carrito, luego activa la regla y sincronízala con Shopify cuando estés listo.'
        },
        {
          question: '¿Cuánto cuesta RuleForge?',
          answer: 'Hay un plan Free con hasta 3 reglas activas y un plan Pro con hasta 20. Los precios vigentes y cualquier periodo de prueba aparecen en la página de planes de Shopify, y los cargos llegan en tu factura de Shopify.'
        },
        {
          question: '¿RuleForge hace más lento mi checkout?',
          answer: 'Los descuentos corren dentro del checkout de Shopify con Shopify Functions. Cada versión se revisa contra los límites de rendimiento que Shopify fija para las Functions, incluidos carritos grandes.'
        },
        {
          question: '¿RuleForge reemplaza a Shopify Scripts?',
          answer: 'No. RuleForge se enfoca en descuentos de producto y envío por reglas. No es una ruta de migración para Shopify Scripts.'
        },
        {
          question: '¿RuleForge hace 2x1, paquetes o precios por volumen?',
          answer: 'Por ahora no. RuleForge ofrece descuentos de producto por porcentaje o monto fijo por pieza, además de envío gratis.'
        },
        {
          question: '¿Los descuentos de RuleForge se combinan con mis otros descuentos de Shopify?',
          answer: 'Por ahora no. La combinación con otras clases de descuento no está habilitada, así que tenlo en cuenta al planear promociones que se crucen.'
        },
        {
          question: '¿A qué datos de mi tienda accede RuleForge?',
          answer: 'RuleForge pide a Shopify permiso para leer productos y para leer y escribir descuentos. No guarda datos personales de tus compradores para marketing. La política de privacidad explica cada detalle.'
        }
      ],
      finalTitle: 'Tu próxima promoción puede funcionar sola',
      finalBody: 'Instala RuleForge, crea tu primera regla en minutos y deja que el checkout haga el resto.',
      risk: ['Plan gratis, no guardamos tu tarjeta', 'Las reglas empiezan apagadas hasta que las actives', 'Desinstala cuando quieras desde el admin de Shopify'],
      resourceTitle: 'Ayuda y políticas',
      resources: [
        {
          key: 'support',
          title: 'Soporte',
          description: 'Guía de configuración, pruebas, solución de problemas y contacto directo.'
        },
        {
          key: 'privacy',
          title: 'Política de privacidad',
          description: 'Qué procesa RuleForge, por qué es necesario y cómo se atienden solicitudes de datos.'
        },
        {
          key: 'terms',
          title: 'Términos de servicio',
          description: 'Las reglas y responsabilidades que aplican al usar RuleForge.'
        }
      ]
    },
    support: {
      metaTitle: 'Soporte de RuleForge | Ayuda para comercios Shopify',
      metaDescription: 'Obtén ayuda para configurar, probar y resolver problemas con los descuentos automáticos de RuleForge.',
      eyebrow: 'Soporte de RuleForge',
      title: 'Haz que tus reglas funcionen con confianza.',
      intro: 'Usa esta guía para configurar, probar en checkout y resolver problemas comunes. Si aún necesitas ayuda, escribe al equipo de Jamak e incluye el dominio de tu tienda Shopify.',
      contactTitle: '¿Necesitas ayuda personal?',
      contactBody: 'Escríbenos con el dominio de tu tienda, lo que esperabas y lo que ocurrió. No envíes contraseñas, datos de pago ni tokens de acceso de Shopify.',
      contactCta: 'Enviar email a soporte',
      sections: [
        {
          heading: 'Primeros pasos',
          ordered: true,
          items: [
            'Instala RuleForge y ábrelo desde Shopify Admin.',
            'Crea un grupo de clientes y agrega las etiquetas de Shopify que determinan quién califica.',
            'Agrega uno o más niveles de descuento de producto, una regla de envío o ambos.',
            'Revisa las exclusiones, guarda los cambios y confirma que las verificaciones del sistema estén listas.'
          ]
        },
        {
          heading: 'Prueba antes de promover una oferta',
          paragraphs: [
            'Usa una cuenta de cliente con la etiqueta configurada, agrega un producto elegible al carrito y continúa al checkout. Confirma el mensaje de descuento de producto y cualquier descuento de envío antes de publicar la oferta.',
            'Los descuentos automáticos no se aplican por defecto a pedidos preliminares de Shopify. En un pedido preliminar, elige “Aplicar todos los descuentos automáticos elegibles” antes de comparar el resultado.'
          ]
        },
        {
          heading: 'Si un descuento no aparece',
          items: [
            'Confirma que la regla y el nivel correspondiente estén activos.',
            'Confirma que el cliente de prueba inició sesión y tiene exactamente la etiqueta usada por la regla.',
            'Verifica que el subtotal alcance el mínimo configurado.',
            'Revisa las exclusiones globales y las específicas del grupo.',
            'Si seleccionas productos por etiqueta o variante, confirma que el artículo del carrito coincida.',
            'Abre Verificaciones del sistema en RuleForge y usa Sincronizar reglas si el descuento automático o la Function requieren atención.'
          ]
        },
        {
          heading: 'Antes de contactar a soporte',
          items: [
            'Anota el dominio de la tienda y la hora aproximada de la prueba.',
            'Comparte el nombre de la regla, el descuento esperado y el producto o variante probado.',
            'Incluye una captura de las verificaciones del sistema y del checkout cuando sea posible.',
            'Nunca incluyas credenciales, tokens de acceso ni información completa de pago de clientes.'
          ]
        },
        {
          heading: 'Solicitudes de datos y eliminación',
          paragraphs: [
            'Para preguntas de privacidad o solicitudes sobre datos del comercio, escribe a hello@jamaklab.com desde una dirección asociada con la tienda. Las solicitudes de datos de clientes Shopify se atienden mediante el proceso de cumplimiento requerido por Shopify.'
          ]
        }
      ]
    },
    privacy: {
      metaTitle: 'Política de privacidad de RuleForge | JAMAK',
      metaDescription: 'Conoce qué datos procesa RuleForge, cómo se usan y cómo Jamak atiende solicitudes de privacidad y eliminación.',
      eyebrow: 'Privacidad de RuleForge',
      title: 'Política de privacidad',
      intro: 'Esta política explica cómo RuleForge, una app de Shopify ofrecida por Jamak, procesa información cuando un comercio instala o usa la app.',
      sections: [
        {
          heading: 'Información que procesamos',
          items: [
            'Información de la tienda y cuenta proporcionada por Shopify, incluido el dominio, el token de acceso para llamar a la Admin API de Shopify y los registros de instalación o autenticación necesarios para conectar la app. Las sesiones pueden incluir datos de identidad del personal que Shopify entrega al iniciar sesión, como nombre, correo e idioma.',
            'Configuración de reglas, incluidas condiciones por etiqueta de cliente, mínimos de gasto, productos objetivo, exclusiones, valores, mensajes y reglas de envío.',
            'Estado del plan, incluido el plan activo, su límite de reglas y la última vez que se verificó con Shopify.',
            'Carritos de ejemplo que el comercio captura en el simulador de checkout, procesados para calcular la vista previa.',
            'Información técnica y de diagnóstico como horas de solicitudes, errores de la aplicación y eventos de seguridad o disponibilidad necesarios para operar y proteger el servicio.'
          ]
        },
        {
          heading: 'Datos de clientes y pedidos',
          paragraphs: [
            'RuleForge no guarda perfiles de clientes, pedidos ni datos de pago en su propia base de datos. En checkout, Shopify proporciona a la Function configurada el carrito, la información de productos elegibles y las etiquetas de cliente necesarias para evaluar una regla. Esa evaluación ocurre dentro de Shopify Functions.',
            'RuleForge solicita únicamente los permisos de Shopify necesarios para leer productos y descuentos, y crear o actualizar descuentos administrados por la app.'
          ]
        },
        {
          heading: 'Cómo usamos la información',
          items: [
            'Autenticar al comercio y ofrecer la app dentro de Shopify Admin.',
            'Crear, actualizar, validar y diagnosticar la configuración de descuentos automáticos.',
            'Mantener la seguridad, prevenir abuso, diagnosticar errores y mejorar la confiabilidad.',
            'Responder solicitudes de soporte, privacidad y cumplimiento legal.'
          ]
        },
        {
          heading: 'Dónde se guarda la configuración',
          paragraphs: [
            'La configuración se guarda en el descuento automático de Shopify mediante metafields propiedad de la app. Las sesiones de autenticación y el estado del plan se guardan en una base de datos administrada para que la app se comunique de forma segura con Shopify. Shopify controla los datos procesados en su plataforma y en Shopify Functions conforme a sus propios términos y prácticas de privacidad.'
          ]
        },
        {
          heading: 'Cobro',
          paragraphs: [
            'Los planes de pago los cobra Shopify mediante Shopify App Pricing y aparecen en la factura de Shopify del comercio. Nunca recibimos ni guardamos números de tarjeta ni otros datos de pago.'
          ]
        },
        {
          heading: 'Analítica de producto',
          paragraphs: [
            'RuleForge usa PostHog para una analítica de producto limitada. La app registra qué pantalla del admin está abierta; la instalación y desinstalación; la creación, activación, desactivación y sincronización de reglas; y los cambios de plan verificados. Cada tienda se representa con un hash unidireccional con sal, no con su dominio, y las tiendas de desarrollo se marcan con un solo valor de sí o no.',
            'RuleForge no envía a PostHog datos del personal, clientes, pedidos, URLs, formularios, contenido de las reglas de descuento ni montos de cobro. El rastreo automático de clics y páginas, los perfiles de personas, los identificadores persistentes del navegador, la grabación de sesiones, las encuestas y la geolocalización por IP están desactivados.'
          ]
        },
        {
          heading: 'Monitoreo de errores',
          paragraphs: [
            'Usamos Sentry para recibir reportes cuando la app encuentra un error. Los reportes se limitan al tipo de error, el lugar del código donde ocurrió, la versión de la app y códigos operativos breves. Excluyen la identidad del usuario, cookies, encabezados y cuerpos de solicitudes y respuestas, parámetros de URL, datos de la base de datos y grabación de sesiones.'
          ]
        },
        {
          heading: 'Proveedores y divulgación',
          paragraphs: [
            'Compartimos información solo con los proveedores necesarios para operar RuleForge: Shopify (plataforma, autenticación, checkout y cobro), Railway (hosting de la aplicación y base de datos administrada), Sentry (monitoreo de errores) y PostHog (analítica de producto), cada uno con los límites descritos arriba. Pueden procesar información únicamente para prestarnos esos servicios. También podemos divulgar información si la ley lo exige, para proteger derechos y seguridad, o como parte de una operación empresarial sujeta a salvaguardas adecuadas.',
            'No vendemos información personal de comercios ni clientes.'
          ]
        },
        {
          heading: 'Retención y eliminación',
          paragraphs: [
            'Conservamos autenticación y registros operativos solo durante el tiempo razonablemente necesario para ofrecer RuleForge, cumplir obligaciones legales, resolver disputas y proteger el servicio. Después de desinstalar, Shopify notifica a RuleForge y la app elimina la sesión activa de esa tienda. Cuando Shopify solicita la eliminación de datos de la tienda, la app borra las sesiones y registros de cobro restantes de esa tienda. La configuración propiedad de la app almacenada en Shopify queda sujeta al funcionamiento de la plataforma y a los controles del comercio.',
            'Las solicitudes verificadas de acceso y eliminación pueden enviarse a hello@jamaklab.com. También procesamos los webhooks obligatorios de privacidad de tiendas y clientes de Shopify.'
          ]
        },
        {
          heading: 'Seguridad y procesamiento internacional',
          paragraphs: [
            'Usamos medidas administrativas, técnicas y organizacionales diseñadas para proteger la información. Ningún sistema es completamente seguro y no podemos garantizar seguridad absoluta.',
            'Jamak y sus proveedores pueden procesar información en países distintos al del comercio. Cuando corresponde, usamos salvaguardas adecuadas para esas transferencias.'
          ]
        },
        {
          heading: 'Tus opciones y derechos',
          paragraphs: [
            'Según la ley aplicable, puedes tener derecho a solicitar acceso, corrección, eliminación, restricción o una copia de información personal. Podemos verificar tu identidad y autoridad sobre la tienda antes de responder.'
          ]
        },
        {
          heading: 'Cambios y contacto',
          paragraphs: [
            'Podemos actualizar esta política cuando cambien RuleForge o los requisitos aplicables. La fecha vigente identifica la versión más reciente. Envía preguntas y solicitudes a hello@jamaklab.com.'
          ]
        }
      ]
    },
    terms: {
      metaTitle: 'Términos de servicio de RuleForge | JAMAK',
      metaDescription: 'Consulta los términos aplicables cuando un comercio instala y usa la app RuleForge para Shopify.',
      eyebrow: 'Términos de RuleForge',
      title: 'Términos de servicio',
      intro: 'Estos términos rigen el acceso y uso de RuleForge, una app de Shopify ofrecida por Jamak. Al instalar o usar RuleForge, aceptas estos términos.',
      sections: [
        {
          heading: 'Elegibilidad y autoridad',
          paragraphs: [
            'Debes tener capacidad legal para aceptar estos términos y autorización para actuar por la tienda Shopify que instala RuleForge. Eres responsable de cualquier persona que acceda a la app mediante la cuenta de tu tienda.'
          ]
        },
        {
          heading: 'El servicio',
          paragraphs: [
            'RuleForge ayuda a configurar descuentos automáticos de producto y envío ejecutados mediante Shopify Functions. No reemplaza a Shopify Scripts, no ofrece promociones 2x1, paquetes ni precios por volumen, y sus descuentos no se combinan con otras clases de descuento de Shopify. Las funciones pueden evolucionar conforme mejoramos la app y algunas pueden identificarse como beta o acceso anticipado.'
          ]
        },
        {
          heading: 'Acceso y permisos de Shopify',
          paragraphs: [
            'Autorizas a RuleForge a usar los permisos de Shopify mostrados durante la instalación para prestar el servicio. Shopify es un proveedor independiente y tu uso de Shopify sigue sujeto a sus propios acuerdos y políticas.'
          ]
        },
        {
          heading: 'Responsabilidades del comercio',
          items: [
            'Configurar reglas correctas y probarlas antes de anunciar o depender de una oferta.',
            'Asegurar que descuentos, precios, marketing y comunicaciones cumplan las leyes aplicables y tus propias políticas.',
            'Mantener segura la cuenta de Shopify y reportar rápidamente cualquier acceso no autorizado.',
            'Proporcionar información correcta al solicitar soporte.'
          ]
        },
        {
          heading: 'Precios',
          paragraphs: [
            'RuleForge ofrece un plan Free con hasta 3 reglas activas y un plan Pro con hasta 20. Los planes de pago se cobran únicamente mediante Shopify App Pricing y aparecen en tu factura de Shopify; no hay cargos fuera de Shopify. Los precios vigentes y cualquier periodo de prueba se muestran en la página de planes de Shopify antes de que apruebes un cargo, y puedes cambiar o cancelar tu plan desde Shopify Admin. Si un plan de pago no puede verificarse con Shopify, la app vuelve a los límites del plan Free.'
          ]
        },
        {
          heading: 'Uso aceptable',
          paragraphs: [
            'No puedes usar RuleForge indebidamente, interferir con su operación, intentar acceso no autorizado, aplicar ingeniería inversa salvo donde la ley lo permita, usarlo para violar leyes o derechos de terceros, ni enviar código o contenido malicioso.'
          ]
        },
        {
          heading: 'Disponibilidad y cambios',
          paragraphs: [
            'Buscamos ofrecer un servicio confiable, pero RuleForge puede no estar disponible por mantenimiento, seguridad, cambios de Shopify o eventos fuera de nuestro control. Podemos modificar o retirar funciones y, cuando sea razonablemente posible, avisaremos sobre cambios importantes.'
          ]
        },
        {
          heading: 'Propiedad y comentarios',
          paragraphs: [
            'Jamak y sus licenciantes conservan todos los derechos sobre RuleForge, excepto el contenido y los datos del comercio. Recibes un derecho limitado, no exclusivo e intransferible para usar la app mientras estos términos estén vigentes. Si envías comentarios, nos permites usarlos sin restricción ni compensación.'
          ]
        },
        {
          heading: 'Terminación',
          paragraphs: [
            'Puedes dejar de usar RuleForge en cualquier momento desinstalándolo. Podemos suspender o terminar el acceso si incumples materialmente estos términos, generas un riesgo legal o de seguridad, o continuar la operación deja de ser práctico. Las disposiciones que por su naturaleza deban sobrevivir seguirán vigentes.'
          ]
        },
        {
          heading: 'Exclusiones de garantía',
          paragraphs: [
            'En la medida permitida por la ley, RuleForge se proporciona “tal cual” y “según disponibilidad”. Excluimos garantías implícitas, incluidas comerciabilidad, idoneidad para un propósito particular y no infracción. No garantizamos que toda configuración produzca un resultado comercial específico ni que sea compatible con cada servicio de terceros.'
          ]
        },
        {
          heading: 'Limitación de responsabilidad',
          paragraphs: [
            'En la máxima medida permitida por la ley, Jamak no será responsable de daños indirectos, incidentales, especiales, consecuentes, ejemplares o punitivos, ni de pérdida de utilidades, ingresos, datos, reputación u oportunidades derivadas de RuleForge. Nuestra responsabilidad total por el servicio no excederá el mayor entre el monto pagado por RuleForge durante los doce meses anteriores al evento que originó la reclamación o USD $100.'
          ]
        },
        {
          heading: 'Ley aplicable y contacto',
          paragraphs: [
            'Estos términos se rigen por las leyes aplicables de México, sin considerar principios sobre conflicto de leyes. Envía preguntas sobre estos términos a hello@jamaklab.com.'
          ]
        }
      ]
    }
  }
};

export const getAppsContent = (language) => (
  appsContent[language === 'es' ? 'es' : 'en']
);
